import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import {
    knowledgeApi,
    KnowledgeAudience,
    KnowledgeScope,
} from '../../../api/knowledgeApi';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: () => void | Promise<void>;
    professionOptions: { key: string; label: string }[];
}

const SUPPORTED_LANGUAGES = ['EN', 'NL', 'PL', 'RO', 'DE', 'IT', 'ES', 'FR', 'HU'];

const ACCEPTED_MIMES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'text/plain',
    'text/markdown',
].join(',');

/**
 * Add-entry modal — three tabs (Upload file / Paste text / URL), shared scope editor.
 *
 * The URL tab pastes a URL into a text field and submits; the backend
 * round-trips to optima-ai which fetches with robots.txt check + content-type
 * sniff, then we get a normal `INDEXING → READY` entry back. Slow URLs may
 * take a few seconds to extract; the submit button shows a spinner the whole
 * time.
 */
export const AddKnowledgeModal: React.FC<Props> = ({
    open,
    onClose,
    onCreated,
    professionOptions,
}) => {
    const [tab, setTab] = useState<0 | 1 | 2>(0);
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [url, setUrl] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [professions, setProfessions] = useState<string[]>([]);
    const [languages, setLanguages] = useState<string[]>([...SUPPORTED_LANGUAGES]);
    const [audience, setAudience] = useState<KnowledgeAudience>('WORKER');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setTab(0);
        setTitle('');
        setText('');
        setUrl('');
        setFile(null);
        setProfessions([]);
        setLanguages([...SUPPORTED_LANGUAGES]);
        setAudience('WORKER');
        setError(null);
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);
        if (!title.trim()) {
            setError('Title is required.');
            return;
        }
        if (tab === 0 && !file) {
            setError('Pick a file to upload, or switch to a different tab.');
            return;
        }
        if (tab === 1 && !text.trim()) {
            setError('Paste the text you want to ingest.');
            return;
        }
        if (tab === 2) {
            const trimmed = url.trim();
            if (!trimmed) {
                setError('Paste the URL you want to ingest.');
                return;
            }
            // Parse with the URL constructor — catches missing host
            // (`https://` alone), trailing-whitespace junk, and non-http(s)
            // schemes in one shot. Backend / optima-ai still re-validate.
            let parsed: URL;
            try {
                parsed = new URL(trimmed);
            } catch {
                setError('That doesn\'t look like a valid URL. Example: https://optimauitzendburo.nl/about');
                return;
            }
            if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
                setError('URL must start with https:// (or http:// for testing only).');
                return;
            }
            if (!parsed.host) {
                setError('URL is missing the host. Example: https://optimauitzendburo.nl/about');
                return;
            }
        }

        const scope: KnowledgeScope = {
            audience,
            profession: professions.length ? professions : undefined,
            language: languages.length ? languages : undefined,
        };

        setSubmitting(true);
        try {
            if (tab === 0 && file) {
                await knowledgeApi.uploadFile({ title: title.trim(), file, scope });
            } else if (tab === 1) {
                await knowledgeApi.createFromPaste({ title: title.trim(), text, scope });
            } else {
                await knowledgeApi.createFromUrl({ title: title.trim(), url: url.trim(), scope });
            }
            reset();
            await onCreated();
        } catch (err: any) {
            // Optima-ai surfaces structured errors like {error_code, detail}
            // through Nest's HttpException; both shapes show up here.
            const detail = err?.response?.data?.detail;
            const msg =
                (typeof detail === 'object' && detail?.detail) ||
                (typeof detail === 'string' ? detail : null) ||
                err?.response?.data?.message ||
                err?.message;
            setError(typeof msg === 'string' ? msg : 'Failed to create entry');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Add knowledge entry</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        autoFocus
                        size="small"
                        helperText="Shown in the admin list; not visible to workers."
                    />

                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tab label="Upload file" />
                        <Tab label="Paste text" />
                        <Tab label="URL" icon={<LinkIcon fontSize="small" />} iconPosition="start" />
                    </Tabs>

                    {tab === 0 && (
                        <Box>
                            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                                {file ? file.name : 'Choose file'}
                                <input
                                    hidden
                                    type="file"
                                    accept={ACCEPTED_MIMES}
                                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                                />
                            </Button>
                            <Box sx={{ mt: 1 }}>
                                <Chip label="PDF" size="small" sx={{ mr: 0.5 }} />
                                <Chip label="DOCX" size="small" sx={{ mr: 0.5 }} />
                                <Chip label="DOC" size="small" sx={{ mr: 0.5 }} />
                                <Chip label="TXT" size="small" sx={{ mr: 0.5 }} />
                                <Chip label="MD" size="small" />
                            </Box>
                        </Box>
                    )}

                    {tab === 1 && (
                        <TextField
                            label="Text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            multiline
                            minRows={8}
                            maxRows={20}
                            fullWidth
                            placeholder="Paste the content you want the bot to be able to answer about."
                        />
                    )}

                    {tab === 2 && (
                        <Box>
                            <TextField
                                label="URL"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="https://optimauitzendburo.nl/about"
                                helperText="https:// only. The page's main text is extracted (nav / footer / scripts stripped). PDFs at a URL are also supported."
                            />
                            <Box sx={{ mt: 1 }}>
                                <Chip label="HTML" size="small" sx={{ mr: 0.5 }} />
                                <Chip label="PDF (linked)" size="small" sx={{ mr: 0.5 }} />
                                <Chip label="TXT / MD" size="small" />
                            </Box>
                        </Box>
                    )}

                    <Box>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="kb-audience-label">Audience</InputLabel>
                            <Select
                                labelId="kb-audience-label"
                                label="Audience"
                                value={audience}
                                onChange={(e) => setAudience(e.target.value as KnowledgeAudience)}
                            >
                                <MenuItem value="WORKER">Worker — bot can use this when talking to workers</MenuItem>
                                <MenuItem value="ADMIN">Admin — for internal staff tooling (not bot-facing yet)</MenuItem>
                                <MenuItem value="BOTH">Both</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <FormControl size="small" fullWidth>
                        <InputLabel id="kb-prof-label">Profession scope (optional)</InputLabel>
                        <Select
                            labelId="kb-prof-label"
                            label="Profession scope (optional)"
                            multiple
                            value={professions}
                            onChange={(e) => setProfessions(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {selected.map((p) => <Chip key={p} size="small" label={p} />)}
                                </Stack>
                            )}
                        >
                            {professionOptions.map((p) => (
                                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                        <InputLabel id="kb-lang-label">Language scope (optional)</InputLabel>
                        <Select
                            labelId="kb-lang-label"
                            label="Language scope (optional)"
                            multiple
                            value={languages}
                            onChange={(e) => setLanguages(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {selected.map((l) => <Chip key={l} size="small" label={l} />)}
                                </Stack>
                            )}
                        >
                            {SUPPORTED_LANGUAGES.map((l) => (
                                <MenuItem key={l} value={l}>{l}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                    {submitting
                        ? (tab === 2 ? 'Fetching & saving…' : 'Saving…')
                        : (tab === 2 ? 'Fetch & save' : 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
